import { DateTime } from 'luxon';
export * from './rrule';
export * from './types';

export function dateToISODateString(date: Date) {
  return date.toISOString().split('T')[0];
}

/**
 * Returns YYYY-MM-DD,
 * with the local timezone.
 */
export function dateToLocaleDateString(date: Date, tz: string) {
  return date.toLocaleDateString('en-CA', {
    timeZone: tz,
  });
}

export function isSameDay(date1?: Date, date2?: Date) {
  if (!date1 || !date2) {
    return false;
  }
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
}

/**
 * Adjusts the date component of the first argument with the time component of the second argument.
 * Can optionally take into account the timezone and its potential change of DST between the two dates.
 *
 * @param date The date whose time component needs to be adjusted.
 * @param time The date from which the time component will be extracted.
 * @param tzId The timezone ID (e.g., 'America/New_York'). If provided, operations will be timezone-aware.
 */
export function applyTimeOfDate(date: Date, time: Date, tzId?: string): Date {
  const luxonDate = DateTime.fromJSDate(date, { zone: tzId });
  const luxonTime = DateTime.fromJSDate(time, { zone: tzId });

  // Combine date from luxonDate and time from luxonTime
  const combined = luxonDate.set({
    hour: luxonTime.hour,
    minute: luxonTime.minute,
    second: luxonTime.second,
    millisecond: luxonTime.millisecond,
  });

  // If a timezone was specified, return the date in that timezone
  // Otherwise, return the date in the system's local timezone
  return tzId ? combined.toJSDate() : combined.toLocal().toJSDate();
}
/**
 * Adjusts a UTC Date object to reflect the time in a specified timezone.
 *
 * ⚠: This adjustment only affects the representation of the Date object,
 * not its inherent UTC value. Using this function can lead to misleading
 * results when the Date object is later interpreted as UTC. Use with caution.
 * It should probably only be used when the date needs to be passed to other
 * libraries like RRule.
 *
 * @param date The UTC date to adjust.
 * @param tzId The target timezone ID.
 * @returns A new Date object representing the adjusted date-time.
 */
export function adjustUtcDateToTimezone(date: Date, tzId: string): Date {
  const dateTime = DateTime.fromJSDate(new Date(date), { zone: 'utc' }).setZone(tzId);
  const offsetInMinutes = dateTime.offset;
  return new Date(dateTime.toMillis() - offsetInMinutes * 60 * 1000);
}

/**
 * ⚠: This function reverts the adjustment made by adjustUtcDateToTimezone.
 * The function adds the timezone offset to a given date to revert the previous subtraction.
 */
export function revertAdjustedTimezone(date: Date, tzId: string): Date {
  const luxonDate = DateTime.fromJSDate(date, { zone: 'utc' });
  const offsetInMilliseconds = luxonDate.setZone(tzId).offset * 60 * 1000; // Convert offset from minutes to milliseconds
  const revertedDate = new Date(date.getTime() + offsetInMilliseconds);
  return revertedDate;
}

export function toLocalIsoString(date: Date) {
  const pad = (n: number) => `${Math.floor(Math.abs(n))}`.padStart(2, '0');
  return (
    date.getFullYear() +
    '-' +
    pad(date.getMonth() + 1) +
    '-' +
    pad(date.getDate()) +
    'T' +
    pad(date.getHours()) +
    ':' +
    pad(date.getMinutes()) +
    ':' +
    pad(date.getSeconds())
  );
}

export function timeStrToDate(timeStr: string) {
  const time = timeStr.split(/:|-/g);
  const date = new Date();
  date.setHours(parseInt(time[0], 10));
  date.setMinutes(parseInt(time[1], 10));
  return date;
}

export function isBetweenTimeRange(from: string, to: string) {
  const fromDate = timeStrToDate(from);
  const toDate = timeStrToDate(to);
  const now = Date.now();
  return now >= fromDate.getTime() && now < toDate.getTime();
}

/**
 * Is the given date string in the format YYYY-MM-DD,
 * and does not contain any timezone/time information?
 */
export function isRelativeISODateString(dateStr: string) {
  return !dateStr.includes('Z') && !dateStr.includes('+') && !dateStr.includes('T');
}

/**
 * Set to start of day usning Luxon, taking into account the timezone.
 */
export function toStartOfDay(date: Date, tzId?: string) {
  const luxonDate = DateTime.fromJSDate(date, { zone: tzId });
  const startOfDay = luxonDate.startOf('day');
  return startOfDay.toJSDate();
}

export function toEndOfDay(date: Date, tzId?: string) {
  const luxonDate = DateTime.fromJSDate(date, { zone: tzId });
  const endOfDay = luxonDate.endOf('day');
  return endOfDay.toJSDate();
}

export function getTimezoneOffset(date: Date, tzId?: string) {
  const luxonDate = DateTime.fromJSDate(date, { zone: tzId });
  return luxonDate.offset;
}

export function dateFromHHMM(hhmm: string, origDate?: Date) {
  const [hours, minutes] = hhmm.split(':');
  const date = origDate ? new Date(origDate) : new Date();

  let hoursNum = Number(hours);
  const minutesNum = Number(minutes);

  if (hoursNum === 24) {
    hoursNum = 0;
  }

  date.setHours(hoursNum);
  date.setMinutes(minutesNum);
  date.setSeconds(0);
  date.setMilliseconds(0);

  return date;
}

export function dateAtMidnight(tzId = 'Europe/Berlin') {
  const luxonDate = DateTime.now();
  const date = luxonDate.setZone(tzId);
  return date.startOf('day').toJSDate();
}

/**
 * Output: '2024-05-24'
 */
export function toLocaleDateString(date: Date, tzId = 'Europe/Berlin'): string {
  const luxonDate = DateTime.fromJSDate(date, { zone: tzId });
  luxonDate.startOf('day');

  // assemble string
  const year = luxonDate.year;
  const month = luxonDate.month.toString().padStart(2, '0');
  const day = luxonDate.day.toString().padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * Output: '08:15:00'
 */
export function toLocaleTimeString(date: Date, tzId = 'Europe/Berlin'): string {
  const luxonDate = DateTime.fromJSDate(date, { zone: tzId });

  // assemble string
  const hours = luxonDate.hour.toString().padStart(2, '0');
  const minutes = luxonDate.minute.toString().padStart(2, '0');
  const seconds = luxonDate.second.toString().padStart(2, '0');

  return `${hours}:${minutes}:${seconds}`;
}

/**
 * Assume a date and a string in the format 'HH:mm'.
 * Provided a timezone, return a Date object with the time set to the provided time,
 * in the correct timezone.
 */
export function getLocalDateTime(date: Date, time: string, tzId = 'Europe/Berlin'): Date {
  const luxonDate = DateTime.fromJSDate(date, { zone: tzId });
  const [hours, minutes] = time.split(':');
  const newDate = luxonDate.set({
    hour: Number(hours),
    minute: Number(minutes),
  });
  return newDate.toJSDate();
}
